import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, HelpCircle, Sparkles, TrendingUp, Clock, Ban, Loader2, AlertTriangle } from "lucide-react";

const BAND = {
  go: { label: "Pursue", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", bar: "bg-emerald-500" },
  review: { label: "Review", cls: "text-[#003E87] bg-[#003E87]/10 border-[#003E87]/30", bar: "bg-[#F4C400]" },
  pass: { label: "No-bid", cls: "text-rose-700 bg-rose-50 border-rose-200", bar: "bg-rose-500" },
};

const bandOf = (score, t) => {
  if (score === null || score === undefined) return null;
  if (score >= (t?.go ?? 75)) return "go";
  if (score >= (t?.review ?? 40)) return "review";
  return "pass";
};

// The step immediately after a lead lands: a transparent weighted bid/no-bid decision.
export function BidDecisionDialog({ leadId, leadName, open, onOpenChange, onDecided }) {
  const [factors, setFactors] = useState([]);
  const [thresholds, setThresholds] = useState({ go: 75, review: 40 });
  const [scores, setScores] = useState({});
  const [rationale, setRationale] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);

  useEffect(() => {
    if (!open || !leadId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/leads/${leadId}/fit`);
        setFactors(res.data.factors);
        setThresholds(res.data.thresholds);
        const init = {};
        res.data.factors.forEach((f) => {
          const prev = res.data.existing?.factor_scores?.[f.key];
          init[f.key] = prev ?? f.suggested ?? 50;
        });
        setScores(init);
        setContext(res.data.client_context || null);
        setRationale(res.data.existing?.rationale || "");
      } catch {
        toast.error("Could not load the scorecard");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, leadId]);

  // Sol's explicit Pursue / Review / Pass read — advisory; the human still clicks.
  useEffect(() => {
    if (!open || !leadId) return;
    setRec(null);
    setRecLoading(true);
    api.get(`/leads/${leadId}/recommendation`)
      .then((res) => { setRec(res.data); if (res.data.client_context) setContext(res.data.client_context); })
      .catch(() => {})
      .finally(() => setRecLoading(false));
  }, [open, leadId]);

  const usedWeight = factors.reduce((a, f) => a + (scores[f.key] != null ? f.weight : 0), 0);
  const overall = usedWeight > 0
    ? Math.round((factors.reduce((a, f) => a + (scores[f.key] != null ? scores[f.key] * f.weight : 0), 0) / usedWeight) * 10) / 10
    : null;
  const band = bandOf(overall, thresholds);

  const applySuggestions = () => {
    const next = { ...scores };
    factors.forEach((f) => { if (f.suggested != null) next[f.key] = f.suggested; });
    setScores(next);
    toast.success("Applied data-backed suggestions");
  };

  const decide = async (decision) => {
    if (decision === "pass" && !rationale.trim()) {
      toast.error("A no-bid needs a one-line reason.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/leads/${leadId}/decision`, {
        decision, factor_scores: scores, rationale: rationale.trim(),
      });
      const verb = { go: "Pursuing", review: "Flagged for review", pass: "Recorded no-bid" }[decision];
      toast.success(verb);
      onOpenChange(false);
      onDecided?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the decision");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bid-decision-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Bid decision — {leadName}</DialogTitle>
          <DialogDescription>
            Score each factor. The fit total decides the recommendation: pursue {thresholds.go}+, review {thresholds.review}–{thresholds.go - 1}, no-bid under {thresholds.review}.
          </DialogDescription>
        </DialogHeader>

        {/* Client at a glance — auto-pulled */}
        <div data-testid="client-glance" className="grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400"><TrendingUp className="w-3 h-3" /> Win rate</div>
            <div data-testid="client-winrate" className="text-lg font-bold tabular-nums text-black leading-tight">
              {context?.win_rate_pct != null ? `${context.win_rate_pct}%` : "—"}
            </div>
            <div className="text-[10px] text-slate-400">{context?.decided ? `${context.won}/${context.decided} decided` : "no record yet"}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400"><Clock className="w-3 h-3" /> Responds</div>
            <div data-testid="client-responsiveness" className="text-sm font-semibold text-black leading-tight mt-0.5 truncate" title={context?.responsiveness || ""}>
              {context?.responsiveness || "No history"}
            </div>
            <div className="text-[10px] text-slate-400">{context?.reply_rate_pct != null ? `${context.reply_rate_pct}% reply rate` : "in your inbox"}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400"><Ban className="w-3 h-3" /> Cancel rate</div>
            <div data-testid="client-cancelrate" className="text-lg font-bold tabular-nums text-black leading-tight">
              {context?.cancel_rate_pct != null ? `${context.cancel_rate_pct}%` : "—"}
            </div>
            <div className="text-[10px] text-slate-400">{context?.total ? `${context.cancelled}/${context.total} total` : "no record yet"}</div>
          </div>
        </div>

        {/* Recommendation */}
        <div data-testid="sol-recommendation" className="rounded-lg border border-[#003E87]/20 bg-[#003E87]/[0.03] p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#003E87]">
              <Sparkles className="w-3.5 h-3.5" /> Recommendation
            </span>
            {rec?.verdict && (
              <span data-testid="rec-verdict" className={`text-xs font-bold px-2.5 py-1 rounded-full border ${BAND[rec.verdict]?.cls || ""}`}>
                {rec.verdict_label || BAND[rec.verdict]?.label}
              </span>
            )}
          </div>
          {recLoading ? (
            <div data-testid="rec-loading" className="flex items-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading this client's track record…
            </div>
          ) : rec ? (
            <div className="space-y-2">
              {rec.headline && <p data-testid="rec-headline" className="text-sm font-medium text-black">{rec.headline}</p>}
              <ul className="space-y-1">
                {(rec.reasons || []).map((r, i) => (
                  <li key={i} data-testid={`rec-reason-${i}`} className="flex items-start gap-1.5 text-[13px] text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" /><span>{r}</span>
                  </li>
                ))}
              </ul>
              {(rec.watch_outs || []).length > 0 && (
                <ul className="space-y-1 pt-1 border-t border-[#003E87]/10 mt-2">
                  {rec.watch_outs.map((w, i) => (
                    <li key={i} data-testid={`rec-watchout-${i}`} className="flex items-start gap-1.5 text-[13px] text-amber-700">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" /><span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center justify-between pt-1">
                <span className="text-[10px] text-slate-400">{rec.ai_used ? "Advisory only — you make the call" : "Data-only read — you make the call"}</span>
                <button onClick={applySuggestions} data-testid="rec-apply-btn" className="text-xs text-[#003E87] hover:underline flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Fill scores from data
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-1">No recommendation available.</p>
          )}
        </div>

        {/* Live score header */}
        <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50/70 px-4 py-3">
          <div className="text-center shrink-0">
            <div data-testid="bid-decision-score" className="font-display text-3xl font-bold text-black tabular-nums">
              {overall ?? "—"}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Fit score</div>
          </div>
          <div className="flex-1">
            <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${band ? BAND[band].bar : "bg-slate-300"}`} style={{ width: `${overall ?? 0}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              {band ? (
                <span data-testid="bid-decision-band" className={`text-xs font-semibold px-2 py-0.5 rounded border ${BAND[band].cls}`}>
                  Recommends: {BAND[band].label}
                </span>
              ) : <span className="text-xs text-slate-400">Score at least one factor</span>}
              <button onClick={applySuggestions} data-testid="suggest-from-data-btn" className="text-xs text-[#003E87] hover:underline flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Suggest from data
              </button>
            </div>
          </div>
        </div>

        {/* Factors */}
        <div className="space-y-4 max-h-[42vh] overflow-y-auto pr-1 py-1">
          {loading && factors.length === 0 && (
            <div className="space-y-3 animate-pulse" data-testid="bid-decision-loading">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-3 w-40 bg-slate-200 rounded" />
                  <div className="h-2 w-full bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          )}
          {factors.map((f) => (
            <div key={f.key} data-testid={`factor-${f.key}`}>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-black flex items-center gap-1.5">
                  {f.label}
                  <span className="text-[10px] font-mono text-slate-400">{Math.round(f.weight * 100)}%</span>
                </Label>
                <span className="text-sm font-mono tabular-nums text-slate-700 w-8 text-right">{scores[f.key] ?? "—"}</span>
              </div>
              <Slider
                data-testid={`factor-slider-${f.key}`}
                value={[scores[f.key] ?? 0]} min={0} max={100} step={5}
                onValueChange={(v) => setScores((s) => ({ ...s, [f.key]: v[0] }))}
                className="mt-2"
              />
              <p className="text-[11px] text-slate-500 mt-1 flex items-start gap-1">
                <HelpCircle className="w-3 h-3 mt-0.5 shrink-0 text-slate-300" />
                <span>{f.basis || f.help}</span>
              </p>
            </div>
          ))}
        </div>

        <div>
          <Label className="text-xs">Rationale {band === "pass" && <span className="text-rose-600">(required for a no-bid)</span>}</Label>
          <Textarea data-testid="bid-decision-rationale" rows={2} value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Why this call? One line the next reader will thank you for." />
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" data-testid="decision-pass-btn" onClick={() => decide("pass")} disabled={saving}
            className={`border-rose-200 text-rose-700 hover:bg-rose-50 ${rec?.verdict === "pass" ? "ring-2 ring-rose-300" : ""}`}>
            <XCircle className="w-4 h-4 mr-1.5" /> No-bid
          </Button>
          <Button variant="outline" data-testid="decision-review-btn" onClick={() => decide("review")} disabled={saving}
            className={`border-[#003E87]/30 text-[#003E87] hover:bg-[#003E87]/5 ${rec?.verdict === "review" ? "ring-2 ring-[#003E87]/40" : ""}`}>
            <HelpCircle className="w-4 h-4 mr-1.5" /> Flag for review
          </Button>
          <Button data-testid="decision-go-btn" onClick={() => decide("go")} disabled={saving}
            className={`bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold ${rec?.verdict === "go" ? "ring-2 ring-emerald-400" : ""}`}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Pursue this bid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
